import type { ChangeEvent, ReactNode } from 'react';
import { ConfidenceIndicator, getConfidenceLevel } from './ConfidenceIndicator';

/**
 * ConfidenceField — a form field wrapper that combines a label, value
 * (or input), and a ConfidenceIndicator. When confidence is below the
 * low threshold and the field has not been confirmed, a confirmation
 * checkbox is shown that must be checked before the form can be saved.
 *
 * Requirements: 57.14, 57.15, 57.16, 57.17
 */

export interface ConfidenceFieldProps {
  /** Field label text. */
  label: string;
  /** Current field value. */
  value: string | number;
  /** Confidence score from 0 to 1. */
  confidence: number;
  /** Optional change handler — when provided the field is editable. */
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  /** Whether this red-flagged field has been explicitly confirmed. */
  confirmed?: boolean;
  /** Callback when the user confirms a red-flagged field. */
  onConfirm?: () => void;
  /** Threshold at or above which confidence is considered high (default 0.70). */
  highThreshold?: number;
  /** Threshold below which confidence is critically low (default 0.40). */
  lowThreshold?: number;
  /** Optional custom content to render instead of the default input/value. */
  children?: ReactNode;
}

export function ConfidenceField({
  label,
  value,
  confidence,
  onChange,
  confirmed = false,
  onConfirm,
  highThreshold = 0.7,
  lowThreshold = 0.4,
  children,
}: ConfidenceFieldProps) {
  const fieldId = `confidence-field-${label.toLowerCase().replace(/\s+/g, '-')}`;
  const level = getConfidenceLevel(confidence, highThreshold, lowThreshold);
  const needsConfirmation = level === 'low' && !confirmed;

  return (
    <div style={{ marginBottom: '1rem' }} data-testid="confidence-field">
      <label
        htmlFor={fieldId}
        style={{
          display: 'block',
          marginBottom: '0.25rem',
          fontSize: '0.875rem',
          fontWeight: 500,
          color: '#334155',
        }}
      >
        {label}
      </label>

      <ConfidenceIndicator
        confidence={confidence}
        fieldName={label}
        highThreshold={highThreshold}
        lowThreshold={lowThreshold}
      >
        {children ?? (
          onChange ? (
            <input
              id={fieldId}
              type="text"
              value={value}
              onChange={onChange}
              aria-invalid={needsConfirmation}
              aria-describedby={needsConfirmation ? `${fieldId}-confirm` : undefined}
              style={{
                width: '100%',
                padding: '0.4rem 0.75rem',
                borderRadius: '4px',
                border: `1px solid ${level === 'low' ? '#ef4444' : level === 'medium' ? '#facc15' : '#d1d5db'}`,
                fontSize: '0.875rem',
                backgroundColor: 'white',
              }}
            />
          ) : (
            <span
              id={fieldId}
              style={{ fontSize: '0.875rem', color: '#1e293b' }}
            >
              {value}
            </span>
          )
        )}
      </ConfidenceIndicator>

      {needsConfirmation && (
        <div
          id={`${fieldId}-confirm`}
          style={{
            marginTop: '0.35rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
          }}
          role="alert"
        >
          <input
            type="checkbox"
            id={`${fieldId}-confirm-checkbox`}
            checked={confirmed}
            onChange={() => onConfirm?.()}
            aria-label={`Confirm ${label} value is correct`}
            style={{ accentColor: '#ef4444' }}
          />
          <label
            htmlFor={`${fieldId}-confirm-checkbox`}
            style={{
              fontSize: '0.8rem',
              color: '#991b1b',
              fontWeight: 500,
            }}
          >
            I confirm this value is correct
          </label>
        </div>
      )}
    </div>
  );
}
